import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Volume2, User, Bot } from "lucide-react";

interface Message {
  text: string | { german: string; english: string };
  sender: "user" | "ai";
}

interface MessageBubbleProps {
  message: Message;
  onReplay?: (text: string) => void;
  isLatestAi?: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onReplay
}) => {
  const { t } = useTranslation("talent");
  const isUser = message.sender === "user";

  // Parse message text
  let german = "";

  if (typeof message.text === "string") {
    try {
      const parsed = JSON.parse(message.text);
      german = parsed.german;
    } catch {
      german = message.text;
    }
  } else {
    german = message.text.german;
  }

  return (
    <div
      className={cn(
        "flex w-full mb-3 md:mb-4 gap-2 md:gap-3",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      <Avatar
        className={cn(
          "h-7 w-7 md:h-8 md:w-8",
          isUser ? "bg-primary" : "bg-secondary"
        )}
      >
        <AvatarFallback>
          {isUser ? <User size={14} /> : <Bot size={14} />}
        </AvatarFallback>
      </Avatar>

      <div
        className={cn(
          "flex flex-col max-w-[85%] md:max-w-[80%]",
          isUser ? "items-end" : "items-start"
        )}
      >
        <div
          className={cn(
            "px-3 py-1.5 md:px-4 md:py-2 rounded-2xl shadow-sm",
            isUser
              ? "bg-[#d769b3] text-white rounded-tr-none"
              : "bg-white border text-gray-800 rounded-tl-none"
          )}
        >
          <p className="text-xs md:text-sm font-medium leading-relaxed">
            {german}
          </p>
        </div>

        {!isUser && onReplay && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 h-7 px-2 text-gray-400 hover:text-[#d769b3]"
            onClick={() => onReplay(german)}
          >
            <Volume2 size={14} className="mr-1" />
            <span className="text-[10px]">{t("simulation_replay")}</span>
          </Button>
        )}
      </div>
    </div>
  );
};
